import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, DollarSign, Award, Brain, Target, BarChart3, Calendar, ChevronRight, ArrowUp, ArrowDown, Activity, Globe, Building2, MapPin, Instagram, Twitter, Hash, Zap, Shield, Database, Info, X, Home, School } from 'lucide-react';
import { rosterData } from './playerData';
import { useTheme } from '../utils/theme';

const CoachDashboard = () => {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const [selectedAthlete, setSelectedAthlete] = useState('Blake Barker');
  const [activeTab, setActiveTab] = useState('overview');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [projectionMonths, setProjectionMonths] = useState(6);

  // Get current athlete data from dynamic roster
  const currentAthlete = rosterData[selectedAthlete];
  
  // Get roster list for sidebar
  const athleteRoster = Object.values(rosterData).map(player => ({
    name: player.name,
    position: player.position,
    year: player.year,
    slamScore: player.slamScore,
    nilValue: player.nilValue,
    socialFollowers: player.socialFollowers,
    marketRank: player.marketRank,
    trend: player.trend,
    archetype: player.archetype
  })).sort((a, b) => b.slamScore - a.slamScore);

  // Calculate team totals
  const teamTotals = {
    totalPotential: Object.values(rosterData).reduce((sum, player) => sum + player.earningProjections.realistic, 0),
    activeDeals: Object.values(rosterData).reduce((sum, player) => sum + player.activeDeals, 0),
    avgPerAthlete: Math.round(Object.values(rosterData).reduce((sum, player) => sum + player.earningProjections.realistic, 0) / Object.keys(rosterData).length)
  };

  // Training projections using dynamic data
  const trainingProjections = currentAthlete.performanceMetrics ? 
    Object.entries(currentAthlete.performanceMetrics).slice(0, 3).map(([metric, data]) => ({
      metric: metric.charAt(0).toUpperCase() + metric.slice(1).replace(/([A-Z])/g, ' $1'),
      current: typeof data.current === 'number' ? data.current : data.current.toFixed(2),
      projected: (months) => {
        const projected = data.projected(months);
        return typeof projected === 'number' ? projected : projected.toFixed(2);
      },
      unit: metric === 'velocity' ? 'mph' : 
            metric === 'fieldingPct' ? '' : 
            metric === 'popTime' ? 's' : '%'
    })) : [];

  return (
    <div className={`page-container transition-colors duration-200 ${'bg-[#0a0a0a] text-white'}`}>
      {/* Header */}
      <div className={`border-b transition-colors duration-200 flex-shrink-0 ${'border-gray-600'}`}>
        <div className=" mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold">NIL Analytics Dashboard</h1>
              <p className="text-gray-500 text-sm">Virginia Baseball • Coach/AD View</p>
            </div>
            <button className="themePrimary px-4 py-2 text-white rounded-lg font-medium transition-all">
              Export Report
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 py-6 scrollable-content">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Athlete Roster Sidebar */}
          <div className="lg:col-span-1">
            <div className={`rounded-xl p-4 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users size={20} className="text-blue-500" />
                Athlete Roster
              </h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {athleteRoster.map((athlete) => (
                  <button
                    key={athlete.name}
                    onClick={() => setSelectedAthlete(athlete.name)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      selectedAthlete === athlete.name
                        ? 'bg-yellow-200/20 border border-yellow-200'
                        : `${"bg-gray-700 hover:bg-yellow-400/20"} `
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{athlete.name}</div>
                        <div className="text-xs text-gray-400">
                          {athlete.position} • {athlete.year}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-yellow-500">
                          {athlete.slamScore}
                        </div>
                        <div className="text-xs text-gray-400">SLAM</div>
                      </div>
                    </div>
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        Rank #{athlete.marketRank}
                      </span>
                      <span className="text-xs text-green-500">
                        ${(athlete.nilValue / 1000).toFixed(1)}K
                      </span>
                    </div>
                    <div className="mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        athlete.archetype === 'star' ? 'bg-yellow-500/20 text-yellow-500' :
                        athlete.archetype === 'rising' ? 'bg-blue-500/20 text-blue-500' :
                        athlete.archetype === 'solid' ? 'bg-green-500/20 text-green-500' :
                        'bg-gray-600/20 text-gray-400'
                      }`}>
                        {athlete.archetype}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Team Overview */}
            <div className={`rounded-xl p-4 mt-4 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
              <h3 className="text-sm font-semibold text-gray-400 mb-3">Team NIL Summary</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Potential</span>
                    <span className="font-semibold text-green-500">
                      ${(teamTotals.totalPotential / 1000).toFixed(0)}K
                    </span>
                  </div>
                  <div className="h-2 bg-white rounded-full mt-1">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: '68%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Active Deals</span>
                    <span className="font-semibold">{teamTotals.activeDeals}</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Avg Per Athlete</span>
                    <span className="font-semibold">${(teamTotals.avgPerAthlete / 1000).toFixed(1)}K</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            {/* Athlete Header Card */}
            <div className="bg-gradient-to-r from-yellow-500/5 via-transparent to-yellow-500/5 rounded-xl p-6 border border-yellow-500/20">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{currentAthlete.name}</h2>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span>{currentAthlete.position} • {currentAthlete.year}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Zap size={14} className="text-yellow-500" />
                      SLAM Score: {currentAthlete.slamScore}
                    </span>
                    <span>•</span>
                    <span>Market Rank: #{currentAthlete.marketRank}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                    <span className="flex items-center gap-1">
                      <Home size={12} />
                      {currentAthlete.hometown}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <School size={12} />
                      {currentAthlete.major}
                    </span>
                    <span>•</span>
                    <span>GPA: {currentAthlete.gpa}</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(true)}
                  className="themePrimary px-4 py-2 text-white rounded-lg text-sm font-medium transition-all"
                >
                  Full Breakdown
                </button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-4 mt-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-500">
                    ${(currentAthlete.earningProjections.realistic / 1000).toFixed(1)}K
                  </div>
                  <div className="text-xs text-gray-400">12-Month Projection</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <div className="text-2xl font-bold text-blue-500">
                    {(currentAthlete.socialFollowers / 1000).toFixed(1)}K
                  </div>
                  <div className="text-xs text-gray-400">Total Followers</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <div className="text-2xl font-bold text-purple-500">
                    +{currentAthlete.totalBoost}%
                  </div>
                  <div className="text-xs text-gray-400">Performance Boost</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-500">
                    {currentAthlete.activeDeals}
                  </div>
                  <div className="text-xs text-gray-400">Active Partnerships</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg">
              {['overview', 'partnerships', 'projections', 'compliance'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-4 py-2 rounded-md capitalize text-sm font-medium transition-all ${
                    activeTab === tab
                      ? 'themePrimary text-white'
                      : 'text-gray-400 '
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Social Media Performance */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Hash className="text-cyan-500" />
                    Social Media & Engagement
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Instagram className="text-pink-500" />
                        <div>
                          <div className="font-medium">Instagram</div>
                          <div className="text-xs text-gray-400">
                            {currentAthlete.socialMediaData.instagram.followers.toLocaleString()} followers
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-purple-500 font-semibold">
                          ${currentAthlete.socialMediaData.instagram.rate.toFixed(2)}/1K
                        </div>
                        <div className="text-xs text-gray-400">
                          {currentAthlete.socialMediaData.instagram.engagement.toFixed(1)}% engagement
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Twitter className="text-blue-400" />
                        <div>
                          <div className="font-medium">Twitter</div>
                          <div className="text-xs text-gray-400">
                            {currentAthlete.socialMediaData.twitter.followers.toLocaleString()} followers
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-blue-400 font-semibold">
                          ${currentAthlete.socialMediaData.twitter.rate.toFixed(2)}/1K
                        </div>
                        <div className="text-xs text-gray-400">
                          {currentAthlete.socialMediaData.twitter.engagement.toFixed(1)}% engagement
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Hash className="text-white dark:text-gray-300" />
                        <div>
                          <div className="font-medium">TikTok</div>
                          <div className="text-xs text-gray-400">
                            {currentAthlete.socialMediaData.tiktok.followers.toLocaleString()} followers
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-red-500 font-semibold">
                          ${currentAthlete.socialMediaData.tiktok.rate.toFixed(2)}/1K
                        </div>
                        <div className="text-xs text-gray-400">
                          {currentAthlete.socialMediaData.tiktok.engagement.toFixed(1)}% engagement
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-green-500/10 dark:bg-green-500/20 rounded-lg">
                    <div className="text-sm text-green-500">
                      Average Rate: <span className="font-semibold">
                        ${((currentAthlete.socialMediaData.instagram.rate + 
                           currentAthlete.socialMediaData.twitter.rate + 
                           currentAthlete.socialMediaData.tiktok.rate) / 3).toFixed(2)} per 1K
                      </span>
                    </div>
                  </div>
                </div>

                {/* Performance Multipliers */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Award className="text-orange-500" />
                    Performance & Value Multipliers
                  </h3>
                  <div className="space-y-3">
                    {currentAthlete.performanceMultipliers.map((multiplier, index) => (
                      <div key={index} className="p-3 bg-white dark:bg-gray-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{multiplier.factor}</div>
                            <div className="text-xs text-gray-400">{multiplier.value}</div>
                          </div>
                          <div className="text-green-500 font-semibold">
                            +{multiplier.boost}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-4 bg-green-500/10 dark:bg-green-500/20 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Performance Boost:</span>
                      <span className="text-2xl font-bold text-green-500">+{currentAthlete.totalBoost}%</span>
                    </div>
                  </div>
                </div>

                {/* Earning Projections */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 lg:col-span-2">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="text-blue-500" />
                    12-Month Earning Projections
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-500">
                        ${(currentAthlete.earningProjections.optimistic / 1000).toFixed(1)}K
                      </div>
                      <div className="text-sm text-gray-400 mt-2">Projected Maximum</div>
                      <div className="text-xs text-gray-500 mt-1">If everything goes well</div>
                      <div className="mt-3 text-xs text-blue-400 flex items-center justify-center gap-1">
                        <TrendingUp size={14} />
                        Optimistic
                      </div>
                    </div>
                    <div className="bg-green-500/10 dark:bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-500">
                        ${(currentAthlete.earningProjections.realistic / 1000).toFixed(1)}K
                      </div>
                      <div className="text-sm text-gray-400 mt-2">Realistic Target</div>
                      <div className="text-xs text-gray-500 mt-1">Based on current performance</div>
                      <div className="mt-3 text-xs text-green-500 flex items-center justify-center gap-1">
                        <Target size={14} />
                        Most Likely
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-gray-400">
                        ${(currentAthlete.earningProjections.base / 1000).toFixed(1)}K
                      </div>
                      <div className="text-sm text-gray-400 mt-2">Conservative Base</div>
                      <div className="text-xs text-gray-500 mt-1">Minimum expected</div>
                      <div className="mt-3 text-xs text-gray-500 flex items-center justify-center gap-1">
                        <Shield size={14} />
                        Conservative
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'partnerships' && (
              <div className="space-y-6">
                {/* Brand Opportunities */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="text-purple-500" />
                    Brand Partnership Opportunities
                  </h3>
                  <div className="space-y-4">
                    {currentAthlete.brandOpportunities.map((opp, index) => (
                      <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">{opp.brand}</h4>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                opp.tier === 'National' ? 'bg-blue-500/20 text-blue-500' :
                                opp.tier === 'Mid-Market' ? 'bg-purple-500/20 text-purple-500' :
                                'bg-green-500/20 text-green-500'
                              }`}>
                                {opp.tier}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">{opp.requirements}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-green-500">
                              ${(opp.potentialValue / 1000).toFixed(1)}K
                            </div>
                            <div className="text-xs text-gray-400">potential</div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-xs text-gray-500">
                            Timeline: {opp.timeline}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-gray-500">Probability:</div>
                            <div className="w-20 h-2 bg-gray-700 dark:bg-gray-600 rounded-full">
                              <div 
                                className="h-full bg-gradient-to-r from-orange-500 to-green-500 rounded-full"
                                style={{ width: `${opp.probability}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{opp.probability}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Platform Connections */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Database className="text-cyan-500" />
                    Platform Connections
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(currentAthlete.platformConnections).map(([platform, connected]) => (
                      <div key={platform} className="bg-white dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium capitalize">{platform}</h4>
                            <p className="text-xs text-gray-400 mt-1">
                              {connected ? 'Active account' : 'Not connected'}
                            </p>
                          </div>
                          <div className={`w-3 h-3 rounded-full ${
                            connected ? 'bg-green-500' : 'bg-gray-600'
                          }`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'projections' && (
              <div className="space-y-6">
                {/* Performance Projections */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Activity className="text-orange-500" />
                    Performance Projections
                  </h3>
                  
                  {/* Projection Slider */}
                  <div className="mb-6">
                    <label className="text-sm text-gray-400">Projection Timeline: {projectionMonths} months</label>
                    <input
                      type="range"
                      min="0"
                      max="12"
                      value={projectionMonths}
                      onChange={(e) => setProjectionMonths(parseInt(e.target.value))}
                      className="w-full h-2 bg-white dark:bg-gray-700 rounded-lg appearance-none cursor-pointer mt-2"
                      style={{
                        background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${(projectionMonths / 12) * 100}%, #dfdfdf ${(projectionMonths / 12) * 100}%, #dfdfdf 100%)`
                      }}
                    />
                  </div>

                  <div className="space-y-4">
                    {trainingProjections.map((metric, index) => (
                      <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-medium">{metric.metric}</h4>
                            <div className="text-2xl font-bold mt-1">
                              <span className="text-gray-400">{metric.current}</span>
                              <span className="text-gray-600 dark:text-gray-400 mx-2">→</span>
                              <span className="text-green-500">{metric.projected(projectionMonths)}</span>
                              <span className="text-sm text-gray-500 ml-1">{metric.unit}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-sm font-medium ${
                              projectionMonths > 0 ? 'text-green-500' : 'text-gray-500'
                            }`}>
                              {projectionMonths > 0 ? 
                                `+${((metric.projected(projectionMonths) - metric.current) / metric.current * 100).toFixed(1)}%` : 
                                'Current'
                              }
                            </div>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
                            style={{ 
                              width: `${Math.min((metric.projected(projectionMonths) / metric.current) * 50, 100)}%` 
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Earnings Growth Chart */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="text-green-500" />
                    Projected Earnings Growth
                  </h3>
                  <div className="h-64 bg-white dark:bg-gray-700 rounded-lg p-4 relative">
                    <svg className="w-full h-full">
                      <defs>
                        <linearGradient id="earningsGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#10B981" stopOpacity="0.8" />
                          <stop offset="100%" stopColor="#10B981" stopOpacity="0.1" />
                        </linearGradient>
                      </defs>
                      
                      {/* Grid lines */}
                      {[0, 25, 50, 75, 100].map((y) => (
                        <line
                          key={y}
                          x1="0"
                          y1={`${y}%`}
                          x2="100%"
                          y2={`${y}%`}
                          stroke="#dfdfdf"
                          strokeDasharray="4 4"
                        />
                      ))}
                      
                      {/* Growth curve */}
                      <path
                        d={`M 0 ${100 - (currentAthlete.nilValue / 40000 * 100)} 
                            Q 50% ${100 - (currentAthlete.earningProjections.base / 40000 * 100)} 
                            100% ${100 - (currentAthlete.earningProjections.realistic / 40000 * 100)}`}
                        fill="none"
                        stroke="#10B981"
                        strokeWidth="3"
                      />
                      
                      {/* Fill area */}
                      <path
                        d={`M 0 ${100 - (currentAthlete.nilValue / 40000 * 100)} 
                            Q 50% ${100 - (currentAthlete.earningProjections.base / 40000 * 100)} 
                            100% ${100 - (currentAthlete.earningProjections.realistic / 40000 * 100)}
                            L 100% 100 L 0 100 Z`}
                        fill="url(#earningsGradient)"
                      />
                      
                      {/* Data points */}
                      <circle
                        cx="0"
                        cy={`${100 - (currentAthlete.nilValue / 40000 * 100)}%`}
                        r="4"
                        fill="#10B981"
                      />
                      <circle
                        cx="100%"
                        cy={`${100 - (currentAthlete.earningProjections.realistic / 40000 * 100)}%`}
                        r="4"
                        fill="#10B981"
                      />
                    </svg>
                    
                    {/* Y-axis labels */}
                    <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500 -ml-8">
                      <span>$40K</span>
                      <span>$30K</span>
                      <span>$20K</span>
                      <span>$10K</span>
                      <span>$0</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'compliance' && (
              <div className="space-y-6">
                {/* Compliance Status */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Shield className="text-green-500" />
                    Compliance Status
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <h4 className="font-medium">Academic Eligibility</h4>
                      </div>
                      <p className="text-sm text-gray-400">GPA: {currentAthlete.gpa} - Good Standing</p>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <h4 className="font-medium">NIL Disclosure</h4>
                      </div>
                      <p className="text-sm text-gray-400">All deals reported</p>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <h4 className="font-medium">Training Compliance</h4>
                      </div>
                      <p className="text-sm text-gray-400">100% attendance</p>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <h4 className="font-medium">Social Media</h4>
                      </div>
                      <p className="text-sm text-gray-400">Review recommended</p>
                    </div>
                  </div>
                </div>

                {/* Risk Assessment */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Info className="text-blue-500" />
                    Risk Assessment
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <span className="text-sm">Academic Risk</span>
                      <span className="text-xs px-2 py-1 bg-green-500/20 text-green-500 rounded-full">Low</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <span className="text-sm">Eligibility Risk</span>
                      <span className="text-xs px-2 py-1 bg-green-500/20 text-green-500 rounded-full">Low</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <span className="text-sm">Brand Risk</span>
                      <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-500 rounded-full">Medium</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <span className="text-sm">Performance Risk</span>
                      <span className="text-xs px-2 py-1 bg-green-500/20 text-green-500 rounded-full">Low</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 dark:bg-gray-800 p-6 border-b border-gray-800 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Full Player Analysis - {currentAthlete.name}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-black dark:hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Detailed analysis content */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-3">Player Profile</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Position</span>
                      <span>{currentAthlete.position}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Year</span>
                      <span>{currentAthlete.year}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Hometown</span>
                      <span>{currentAthlete.hometown}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Major</span>
                      <span>{currentAthlete.major}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">GPA</span>
                      <span>{currentAthlete.gpa}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-3">Performance Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">SLAM Score</span>
                      <span className="text-yellow-500 font-bold">{currentAthlete.slamScore}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Market Rank</span>
                      <span>#{currentAthlete.marketRank}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Player Type</span>
                      <span className="capitalize">{currentAthlete.archetype}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Growth Trend</span>
                      <span className="capitalize">{currentAthlete.trend}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Add more detailed sections as needed */}
            </div>
          </div>
        </div>
      )}

      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          background: #3B82F6;
          cursor: pointer;
          border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #3B82F6;
          cursor: pointer;
          border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
          border: none;
        }
      `}</style>
    </div>
  );
};

export default CoachDashboard;